/**
 * In-memory store for the user-side AI assistant (Off / Suggest / Feedback /
 * Both per spec §4.5). Suggestions are produced when it is the player's turn;
 * feedback is produced after each player action and at hand end.
 *
 * Volatile by design — refreshing the page clears this store. Useful state
 * that needs to survive (mode preference) lives in `settingsStore`.
 */
import { create } from 'zustand';
import type { AssistantMode } from './settingsStore';

export type AssistantSuggestion = {
  /** Hand number this suggestion applies to. */
  handNumber: number;
  /** Free-form text shown in the bubble (e.g. "fold to a 3bet"). */
  text: string;
  /** Optional EV estimate the suggester computed (in chips). */
  ev?: number;
  createdAt: number;
};

export type AssistantFeedback = {
  handNumber: number;
  verdict: 'good' | 'okay' | 'mistake';
  text: string;
  createdAt: number;
};

export type AssistantState = {
  mode: AssistantMode;
  suggestions: ReadonlyArray<AssistantSuggestion>;
  feedback: ReadonlyArray<AssistantFeedback>;
  /** Cap on how many entries to keep per channel. */
  maxEntries: number;

  setMode(mode: AssistantMode): void;
  pushSuggestion(suggestion: AssistantSuggestion): void;
  pushFeedback(feedback: AssistantFeedback): void;
  clear(): void;
  setMaxEntries(n: number): void;
};

const DEFAULT_MAX_ENTRIES = 50;

export const useAssistantStore = create<AssistantState>((set) => ({
  mode: 'off',
  suggestions: [],
  feedback: [],
  maxEntries: DEFAULT_MAX_ENTRIES,

  setMode(mode) {
    set({ mode });
  },

  pushSuggestion(suggestion) {
    set((s) => ({
      suggestions: [...s.suggestions, suggestion].slice(-s.maxEntries),
    }));
  },

  pushFeedback(feedback) {
    set((s) => ({
      feedback: [...s.feedback, feedback].slice(-s.maxEntries),
    }));
  },

  clear() {
    set({ suggestions: [], feedback: [] });
  },

  setMaxEntries(n) {
    if (!Number.isFinite(n) || n <= 0) {
      throw new RangeError(`assistantStore.setMaxEntries: n must be positive, got ${n}`);
    }
    set({ maxEntries: n });
  },
}));
